/* eslint-disable no-underscore-dangle */
import React, { useEffect, useState } from 'react';
import Panel from 'components/Panel';
import PanelHeader from 'components/Panel/components/PanelHeader';
import PanelBody from 'components/Panel/components/PanelBody';
import EmptyFolder from 'assets/svg/empty_folder.svg';
import PopupInfoFile from 'routes/DataRoom/components/Popups/PopupInfoFile';
import { Skeleton } from '@mui/material';
import { ROUTE_STARTUP_PATH } from 'routes';
import { addBookmark, deleteBookmark, getFolderContents } from 'routes/DataRoom/modules/actions';
import { getFolderName } from 'routes/DataRoom/components';
import { getFolderIcon, selectType } from 'routes/DataRoom/utils';
import { useLocation, useNavigate } from 'react-router-dom';
import { Folder } from 'lucide-react';
import { useSelector } from 'react-redux';

function SummaryStartupDataroom() {
  const literalsDataroom = useSelector(state => state.i18n.literals.dataRoom);
  const literalsCommon = useSelector(state => state.i18n.literals.common);
  const startup = useSelector(state => state.global.startup);

  const [documents, setDocuments] = useState();
  const [loading, setLoading] = useState(true);
  const [showDocumentPopup, setShowDocumentPopup] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const rolePath = location.pathname.split('/')[1];

  useEffect(() => {
    (async () => {
      const bookmarks = await getFolderContents(rolePath, startup.id, 'bookmark', {
        page: 0, size: 10, sort: '-created_at',
      });
      setDocuments(bookmarks.items);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleItemClick = (item) => (item._type === 'folder'
    ? navigate(ROUTE_STARTUP_PATH.setDataRoom(startup.id, item.id, item.name))
    : setShowDocumentPopup(item));

  const handleBookmark = async (item) => {
    const { id, _type: type, _bookmark: bookmark } = item;
    const element = { id, type };

    if (bookmark) {
      await deleteBookmark(rolePath, startup.id, element);
      setDocuments(prev => prev.filter(bm => bm.id !== element.id));
    } else {
      await addBookmark(rolePath, startup.id, element);
      setDocuments(prev => [item, ...prev]);
    }
  };

  const renderBookmarks = () => {
    if (loading) {
      return [...Array(10)].map((_, i) => (
        <div
          key={`skeleton-bookmark-${i}`}
          className={`bookmark-box col-12 col-md-5 ${i % 2 ? 'offset-md-2' : ''}`}
        >
          <Skeleton className='me-3' animation='wave' width='20px' />
          <Skeleton animation='wave' width='100%' />
        </div>
      ));
    }

    if (!loading && !documents.length) {
      return (
        <div className='empty-folder'>
          <img src={EmptyFolder} alt={literalsDataroom.emptyFolder} />
          <p className='m-0'>{literalsDataroom.emptyBookmarks}</p>
        </div>
      );
    }

    return documents.map((item, index) => (
      <div
        key={`bookmark-${item.id}`}
        className={`bookmark-box col-12 col-md-5 ${index % 2 ? 'offset-md-2' : ''}`}
        onClick={() => handleItemClick(item)}
      >
        {item._type === 'folder' ? (
          <>
            <div className='folder-icon'>
              <Folder size={24} className='me-2' />
              {item.related && (
                <span className='folder-icon--type'>{getFolderIcon(item.related.type, 10)}</span>
              )}
            </div>
            <div className='text-overflow'>{getFolderName(item, literalsDataroom)}</div>
          </>
        ) : (
          <>
            <div className='me-2'>
              {selectType(item.type || item._type, 24)}
            </div>
            <div className='text-overflow'>{item.name}</div>
          </>
        )}
      </div>
    ));
  };

  return (
    <Panel className='summary-dataroom mb-0'>
      <PanelHeader
        title={literalsDataroom.dataRoom}
        subtitle={literalsDataroom.subtitle}
        actions={documents?.length > 10 ? [{
          text: literalsCommon.viewMore,
          onClick: () => navigate(ROUTE_STARTUP_PATH.setDataRoom(startup.id, 'bookmark')),
        }] : null}
      />
      <PanelBody className='h-100'>
        <div className='summary-dataroom-grid'>
          {renderBookmarks()}
        </div>
        {
          showDocumentPopup && (
            <PopupInfoFile
              literals={literalsDataroom}
              literalsCommon={literalsCommon}
              onClose={() => setShowDocumentPopup(null)}
              file={showDocumentPopup}
              setContent={null}
              handleBookmark={handleBookmark}
            />
          )
        }
      </PanelBody>
    </Panel>
  );
}

export default SummaryStartupDataroom;
